// Communication Module Type Definitions

// ============================================
// ENUMS
// ============================================

export enum RecipientType {
  STUDENT = 'STUDENT',
  EMPLOYEE = 'EMPLOYEE',
  PARENT = 'PARENT',
}

export enum MessageType {
  INFO = 'INFO',
  URGENT = 'URGENT',
}

export enum AudienceType {
  ALL_USERS = 'ALL_USERS',        // All students + staff + parents
  ALL_STUDENTS = 'ALL_STUDENTS',
  CLASS_WISE = 'CLASS_WISE',
  ALL_STAFF = 'ALL_STAFF',
  ROLE_WISE = 'ROLE_WISE',
  INDIVIDUAL_USERS = 'INDIVIDUAL_USERS',
}

export enum NotificationType {
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  MESSAGE = 'message',
  FEE_REMINDER = 'FEE_REMINDER',
  SYSTEM = 'SYSTEM',
  APPROVAL = 'APPROVAL',
  EVENT = 'EVENT',
  EXAM_SCHEDULE = 'exam_schedule',
  SYLLABUS_PENDING = 'syllabus_pending',
  SCORES_PENDING = 'scores_pending',
}

export enum NotificationPriority {
  LOW = 'LOW',
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum DeliveryStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
  READ = 'READ',
}

// ============================================
// CORE TYPES
// ============================================

export interface Announcement {
  id: string;
  title: string;
  body: string;
  audience: AudienceType;
  targetDetail?: string; // Class name, department name, or 'All'
  targetIds?: string[]; // Specific class/department IDs
  showAsPopup: boolean;
  createdAt: string; // ISO timestamp
  createdBy: {
    id: string;
    name: string;
    role: string;
  };
  recipientCount: number;
  deliveryStatus: DeliveryStatus;
}

export interface Message {
  id: string;
  recipients: Recipient[];
  content: string;
  type: MessageType;
  showAsPopup: boolean;
  sentAt: string; // ISO timestamp
  sentBy: {
    id: string;
    name: string;
    role: string;
  };
  deliveryStatus: DeliveryStatus;
  readBy?: string[]; // User IDs who have read the message
}

export interface FeeReminder {
  id: string;
  studentIds: string[];
  message: string;
  customMessage?: string;
  sentAt: string; // ISO timestamp
  sentBy: {
    id: string;
    name: string;
    role: string;
  };
  totalDue: number;
  deliveryStatus: DeliveryStatus;
  classId?: string; // If sent to specific class
}

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  relatedEntityId?: string; // ID of announcement, message, etc.
  relatedEntityType?: string; // 'ANNOUNCEMENT', 'MESSAGE', etc.
  actionUrl?: string; // URL to navigate when clicked
  isRead: boolean;
  priority: NotificationPriority;
  createdAt: string; // ISO timestamp
  readAt?: string; // ISO timestamp
  metadata?: Record<string, unknown>; // Additional data
}

// ============================================
// RECIPIENT TYPES
// ============================================

export interface BaseRecipient {
  id: string;
  name: string;
  mobile: string;
  email?: string;
}

export interface StudentRecipient extends BaseRecipient {
  rType: 'STUDENT';
  admissionNo: string;
  classId: string;
  className: string;
  parentMobile?: string;
  parentEmail?: string;
}

export interface EmployeeRecipient extends BaseRecipient {
  rType: 'EMPLOYEE';
  employeeCode: string;
  department: string;
  designation?: string;
}

export type Recipient = StudentRecipient | EmployeeRecipient;

// ============================================
// NOTIFICATION PREFERENCES
// ============================================

export interface NotificationPreferences {
  userId: string;
  email: boolean;
  inApp: boolean;
  push: boolean; // Future feature
  frequency: 'INSTANT' | 'HOURLY' | 'DAILY';
  enabledTypes: {
    [NotificationType.ANNOUNCEMENT]: boolean;
    [NotificationType.MESSAGE]: boolean;
    [NotificationType.FEE_REMINDER]: boolean;
    [NotificationType.APPROVAL]: boolean;
    [NotificationType.EVENT]: boolean;
    [NotificationType.SYSTEM]: boolean;
    [NotificationType.EXAM_SCHEDULE]: boolean;
    [NotificationType.SYLLABUS_PENDING]: boolean;
    [NotificationType.SCORES_PENDING]: boolean;
  };
}

// ============================================
// AUDIENCE SELECTION
// ============================================

export interface AudienceGroup {
  id: string;
  label: string;
  desc: string;
  icon: string; // Emoji
  type: AudienceType;
}

export interface AudienceSelection {
  type: AudienceType;
  targetDetail?: string; // 'All', 'Class 10', 'Science Department'
  targetIds?: string[]; // Specific IDs if class-wise or department-wise
  recipientCount?: number;
}

// ============================================
// MESSAGE TEMPLATES
// ============================================

export interface MessageTemplate {
  id: string;
  label: string;
  content: string;
  category: 'HOMEWORK' | 'EVENT' | 'REMINDER' | 'GENERAL' | 'URGENT';
  usageCount?: number;
  lastUsed?: string;
}

// ============================================
// FEE DEFAULTER
// ============================================

export interface FeeDefaulter {
  studentId: string;
  studentName: string;
  admissionNo: string;
  classId: string;
  className: string;
  mobile: string;
  parentMobile?: string;
  totalFee: number;
  paidFee: number;
  feeBalance: number;
  lastReminderSent?: string; // ISO timestamp
  reminderCount: number;
}

// ============================================
// COMMUNICATION HISTORY
// ============================================

export interface CommunicationHistory {
  id: string;
  type: 'ANNOUNCEMENT' | 'MESSAGE' | 'FEE_REMINDER';
  title: string;
  content: string;
  recipientCount: number;
  sentAt: string;
  sentBy: {
    id: string;
    name: string;
  };
  deliveryStatus: DeliveryStatus;
  readCount?: number;
  failedCount?: number;
}

// ============================================
// API REQUEST/RESPONSE TYPES
// ============================================

export interface CreateAnnouncementRequest {
  title: string;
  body: string;
  audience: AudienceType;
  targetIds?: string[];
  showAsPopup: boolean;
}

export interface SendMessageRequest {
  recipientIds: string[];
  recipientTypes: RecipientType[];
  content: string;
  type: MessageType;
  showAsPopup: boolean;
}

export interface SendFeeReminderRequest {
  studentIds: string[];
  message: string;
  customMessage?: string;
}

export interface GetNotificationsRequest {
  page?: number;
  limit?: number;
  type?: NotificationType;
  isRead?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface GetNotificationsResponse {
  notifications: Notification[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasMore: boolean;
  };
  unreadCount: number;
}

export interface UnreadCountResponse {
  unreadNotifications: number;
  unreadTotalMessages: number;
  unreadConversations: number;
  lastCheckedAt: string;
}

// ============================================
// FILTER TYPES
// ============================================

export interface FeeReminderFilters {
  classIds?: string[];
  minDue?: number;
  maxDue?: number;
  lastReminderBefore?: string; // ISO date
  search?: string;
}

export interface CommunicationFilters {
  type?: 'ANNOUNCEMENT' | 'MESSAGE' | 'FEE_REMINDER';
  startDate?: string;
  endDate?: string;
  status?: DeliveryStatus;
  search?: string;
}

// ============================================
// STATS & ANALYTICS
// ============================================

export interface CommunicationStats {
  announcementsSent: number;
  messagesSent: number;
  feeRemindersSent: number;
  totalRecipients: number;
  pendingFeeReminders: number;
  lastAnnouncementDate?: string;
  lastMessageDate?: string;
  lastFeeReminderDate?: string;
}

// ============================================
// DRAFT TYPES
// ============================================

export interface AnnouncementDraft {
  title: string;
  body: string;
  audience?: AudienceSelection;
  showAsPopup: boolean;
  savedAt: string;
}

export interface MessageDraft {
  recipients: Recipient[];
  content: string;
  type: MessageType;
  showAsPopup: boolean;
  savedAt: string;
}

// ============================================
// DIRECT MESSAGING (CHAT)
// ============================================

export interface Conversation {
  id: string;
  participant: {
    id: string;
    name: string;
    role: 'STUDENT' | 'EMPLOYEE';
    className?: string;   // for students
    department?: string;  // for employees
  };
  lastMessage: {
    content: string;
    sentAt: string;
    sentByMe: boolean;
  } | null;
  unreadCount: number;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  content: string;
  sentBy: { id: string; name: string };
  sentAt: string;
  isRead: boolean;
  isMine: boolean;
}

export interface StartConversationRequest {
  recipientId: string;
}

export interface SendChatMessageRequest {
  content: string;
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
  totalMessages: number;
  isLast: boolean;
}

export interface CommunicationPerson {
  id: string;
  name: string;
  role: 'STUDENT' | 'EMPLOYEE';
  className?: string;
  department?: string;
}
